import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  Bed,
  ChevronRight,
  Coffee,
  Droplets,
  Gift,
  LayoutGrid,
  Lightbulb,
  Lock,
  Search,
  Settings,
  ShoppingBag,
  SlidersHorizontal,
  Snowflake,
  Square,
  Tv,
  User,
  Utensils,
  Wine,
  X,
} from 'lucide-react';
import { Modal } from '../../ui/Modal/Modal';
import { roomAmenities, restaurants } from '../../../data/mockData';
import { ROUTE } from '../../../lib/rcu/catalog/navigation';
import { useRoomStore } from '../../../stores/roomStore';

const deviceIconMap: Record<string, React.ReactNode> = {
  LampDesk: <Lightbulb className="w-5 h-5" />,
  Lamp: <Lightbulb className="w-5 h-5" />,
  Lightbulb: <Lightbulb className="w-5 h-5" />,
  Tv: <Tv className="w-5 h-5" />,
  Monitor: <Tv className="w-5 h-5" />,
  Snowflake: <Snowflake className="w-5 h-5" />,
  Square: <Square className="w-5 h-5" />,
};

const amenityIconMap: Record<string, React.ReactNode> = {
  Wine: <Wine className="w-5 h-5" />,
  Coffee: <Coffee className="w-5 h-5" />,
  Lock: <Lock className="w-5 h-5" />,
  ShoppingBag: <ShoppingBag className="w-5 h-5" />,
  User: <User className="w-5 h-5" />,
  Droplets: <Droplets className="w-5 h-5" />,
  Shirt: <User className="w-5 h-5" />,
  Footprints: <User className="w-5 h-5" />,
};

const pageIconMap: Record<string, React.ReactNode> = {
  home: <LayoutGrid className="w-5 h-5" />,
  controls: <SlidersHorizontal className="w-5 h-5" />,
  guide: <Bed className="w-5 h-5" />,
  dine: <Utensils className="w-5 h-5" />,
  property: <Building className="w-5 h-5" />,
  offers: <Gift className="w-5 h-5" />,
  settings: <Settings className="w-5 h-5" />,
};

export interface SearchModalProps {
  onClose: () => void;
}

interface SearchResult {
  id: string;
  name: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
}

/**
 * Device, amenity, restaurant and page search. Mounted only while open, so the
 * query resets on its own without an effect.
 */
export const SearchModal: React.FC<SearchModalProps> = ({ onClose }) => {
  const { devices } = useRoomStore();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const query = searchQuery.toLowerCase();

  const deviceResults: SearchResult[] = devices
    .filter((device) => device.name.toLowerCase().includes(query))
    .map((device) => ({
      id: device.id,
      name: device.name,
      icon: deviceIconMap[device.icon] || <Lightbulb className="w-5 h-5" />,
      action: () => {
        onClose();
        navigate(ROUTE.CONTROLS);
      },
    }));

  const amenityResults: SearchResult[] = roomAmenities
    .filter(
      (amenity) =>
        amenity.name.toLowerCase().includes(query) ||
        amenity.description.toLowerCase().includes(query),
    )
    .map((amenity) => ({
      id: amenity.id,
      name: amenity.name,
      icon: amenityIconMap[amenity.icon] || <Gift className="w-5 h-5" />,
      action: () => onClose(),
    }));

  const restaurantResults: SearchResult[] = restaurants
    .filter(
      (restaurant) =>
        restaurant.name.toLowerCase().includes(query) ||
        restaurant.cuisine.toLowerCase().includes(query),
    )
    .map((restaurant) => ({
      id: restaurant.id,
      name: restaurant.name,
      subtitle: restaurant.cuisine,
      icon: <Utensils className="w-5 h-5" />,
      action: () => {
        onClose();
        navigate('/dine');
      },
    }));

  const pageResults: SearchResult[] = [
    { id: 'home', name: 'Home', icon: pageIconMap.home, route: '/' },
    { id: 'controls', name: 'Room Controls', icon: pageIconMap.controls, route: ROUTE.CONTROLS },
    { id: 'guide', name: 'Room Guide', icon: pageIconMap.guide, route: '/guide' },
    { id: 'dine', name: 'Dine-In', icon: pageIconMap.dine, route: '/dine' },
    { id: 'property', name: 'Property Info', icon: pageIconMap.property, route: '/property' },
    { id: 'offers', name: 'Offers', icon: pageIconMap.offers, route: '/offers' },
    { id: 'settings', name: 'Settings', icon: pageIconMap.settings, route: '/settings' },
  ]
    .filter((page) => page.name.toLowerCase().includes(query))
    .map((page) => ({
      id: page.id,
      name: page.name,
      icon: page.icon,
      action: () => {
        onClose();
        navigate(page.route);
      },
    }));

  const sections = [
    { title: 'Devices', items: deviceResults },
    { title: 'Amenities', items: amenityResults },
    { title: 'Restaurants', items: restaurantResults },
    { title: 'Pages', items: pageResults },
  ].filter((section) => section.items.length > 0);

  return (
    <Modal isOpen onClose={onClose} title="Search" size="md">
      <div className="py-2">
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#666666]" />
          <input
            type="text"
            autoFocus
            placeholder="Search devices, amenities, restaurants..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-12 w-full rounded-xl bg-[#F5F5F5] pl-12 pr-12 text-[#333333] placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-[#D4A054]"
          />
          {searchQuery !== '' && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#333333]"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {sections.length > 0 ? (
          <div className="max-h-[400px] space-y-1 overflow-y-auto">
            {sections.map((section) => (
              <div key={section.title} className="mb-4">
                <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-[#999999]">
                  {section.title}
                </p>
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={item.action}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-[#F5F5F5]"
                  >
                    <div className="text-[#D4A054]">{item.icon}</div>
                    <div className="min-w-0">
                      <span className="block font-medium text-[#333333]">{item.name}</span>
                      {item.subtitle !== undefined && (
                        <span className="block text-sm text-[#666666]">{item.subtitle}</span>
                      )}
                    </div>
                    <ChevronRight className="ml-auto h-4 w-4 text-[#999999]" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        ) : searchQuery !== '' ? (
          <div className="py-8 text-center">
            <p className="text-[#999999]">No results found for "{searchQuery}"</p>
          </div>
        ) : (
          <div className="py-8 text-center">
            <p className="text-[#999999]">
              Type to search devices, amenities, restaurants, and more
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SearchModal;
